import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import useStore from "../store/useStore";
import api from "../services/api";
import AuthShell, {
  AUTH_LOGO_SRC,
  AuthFieldError,
  AuthFieldHint,
  AuthInputIcon,
  AuthMessage,
  AuthTabs,
  PasswordVisibilityButton,
  Spinner,
  authInputClass,
  authLabelClass,
  authLinkClass,
  authPrimaryButtonClass
} from "../components/auth/AuthUI";
import { COUNTRY_CODE_OPTIONS, buildPhoneWithCountryCode } from "../utils/phoneCountryCodes";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const PASSWORD_RULES = [
  { label: "8+ characters", test: (value) => value.length >= 8 },
  { label: "A number", test: (value) => /\d/.test(value) },
  { label: "Upper & lower case", test: (value) => /[a-z]/.test(value) && /[A-Z]/.test(value) },
  { label: "A symbol", test: (value) => /[^A-Za-z0-9]/.test(value) }
];

const STRENGTH_LEVELS = [
  { label: "Too short", bar: "bg-red-400", text: "text-red-600" },
  { label: "Weak", bar: "bg-orange-400", text: "text-orange-600" },
  { label: "Fair", bar: "bg-amber-400", text: "text-amber-600" },
  { label: "Good", bar: "bg-teal-500", text: "text-teal-700" },
  { label: "Strong", bar: "bg-teal-600", text: "text-teal-700" }
];

const BENEFITS = [
  "Properties, units and tenants in one place",
  "Automated rent invoices and payment tracking",
  "Maintenance, expenses and financial reports",
  "Messaging and notices for your tenants"
];

const NEXT_STEPS = [
  "Share your payment evidence with the Gamrent admin.",
  "We activate your company dashboard.",
  "Sign in with the email you registered."
];

const validate = (data) => {
  const errors = {};
  if (!data.companyName.trim()) errors.companyName = "Enter your company name";
  if (!data.adminFirstName.trim()) errors.adminFirstName = "Enter your first name";
  if (!data.adminLastName.trim()) errors.adminLastName = "Enter your last name";
  if (!data.email.trim()) errors.email = "Enter your email address";
  else if (!EMAIL_PATTERN.test(data.email.trim())) errors.email = "Enter a valid email address";
  if (data.phone && !/^[\d\s()-]{5,}$/.test(data.phone)) errors.phone = "Enter a valid phone number";
  if (!data.password) errors.password = "Create a password";
  else if (data.password.length < 8) errors.password = "Password must be at least 8 characters";
  return errors;
};

const CheckIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
  </svg>
);

const SectionLabel = ({ children }) => (
  <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">{children}</p>
);

const SignupAside = () => (
  <div className="text-white">
    <img src={AUTH_LOGO_SRC} alt="Gamrent" className="w-14 h-14 rounded-2xl shadow-lg shadow-black/20" />
    <h1 className="mt-8 text-4xl font-bold tracking-tight leading-[1.15]">
      Run your rentals with <span className="text-teal-300">clarity</span>
    </h1>
    <p className="mt-4 text-white/65 text-lg leading-relaxed max-w-md">
      Create your company account and manage your whole portfolio from one dashboard.
    </p>

    <ul className="mt-8 space-y-3">
      {BENEFITS.map((item) => (
        <li key={item} className="flex items-start gap-3 text-white/85">
          <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-teal-300/20 text-teal-300">
            <CheckIcon className="w-3 h-3" />
          </span>
          {item}
        </li>
      ))}
    </ul>

    <div className="mt-10 rounded-2xl bg-white/[0.06] border border-white/10 p-5 max-w-md">
      <p className="text-sm font-semibold text-white">How activation works</p>
      <ol className="mt-3 space-y-2.5">
        {NEXT_STEPS.map((step, index) => (
          <li key={step} className="flex items-start gap-3 text-sm text-white/65">
            <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-white/10 text-[11px] font-bold text-white">
              {index + 1}
            </span>
            {step}
          </li>
        ))}
      </ol>
    </div>
  </div>
);

const PasswordStrength = ({ value }) => {
  if (!value) return null;
  const passed = PASSWORD_RULES.filter((rule) => rule.test(value)).length;
  const level = value.length < 8 ? STRENGTH_LEVELS[0] : STRENGTH_LEVELS[passed];

  return (
    <div className="mt-3" aria-live="polite">
      <div className="flex items-center gap-3">
        <div className="flex flex-1 gap-1.5">
          {PASSWORD_RULES.map((rule, index) => (
            <span
              key={rule.label}
              className={`h-1.5 flex-1 rounded-full transition-colors ${index < passed ? level.bar : "bg-slate-200"}`}
            />
          ))}
        </div>
        <span className={`text-xs font-semibold ${level.text}`}>{level.label}</span>
      </div>
      <ul className="mt-2.5 grid grid-cols-2 gap-x-3 gap-y-1.5">
        {PASSWORD_RULES.map((rule) => {
          const ok = rule.test(value);
          return (
            <li key={rule.label} className={`flex items-center gap-1.5 text-xs ${ok ? "text-teal-700" : "text-slate-400"}`}>
              {ok ? <CheckIcon className="w-3.5 h-3.5" /> : <span className="w-3.5 h-3.5 flex items-center justify-center"><span className="w-1 h-1 rounded-full bg-current" /></span>}
              {rule.label}
            </li>
          );
        })}
      </ul>
    </div>
  );
};

const Signup = () => {
  const navigate = useNavigate();
  const { toast } = useStore();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState("");
  const [formData, setFormData] = useState({
    companyName: "",
    adminFirstName: "",
    adminLastName: "",
    email: "",
    countryCode: "+220",
    phone: "",
    password: ""
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: undefined }));
    if (serverError) setServerError("");
  };

  const fieldClass = (name, extra = "") =>
    `${authInputClass} ${extra} ${errors[name] ? "!border-red-300 !bg-red-50/40 focus:!border-red-400 focus:!ring-red-500/10" : ""}`;

  const fieldA11y = (name, hintId) => ({
    "aria-invalid": errors[name] ? true : undefined,
    "aria-describedby": errors[name] ? `signup-${name}-error` : hintId
  });

  const handleSubmit = async (e) => {
    e.preventDefault();

    const nextErrors = validate(formData);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) {
      const firstField = Object.keys(nextErrors)[0];
      document.querySelector(`[name="${firstField}"]`)?.focus();
      return;
    }

    try {
      setLoading(true);
      setServerError("");
      const payload = {
        companyName: formData.companyName.trim(),
        adminFirstName: formData.adminFirstName.trim(),
        adminLastName: formData.adminLastName.trim(),
        email: formData.email.trim(),
        phone: buildPhoneWithCountryCode(formData.countryCode, formData.phone),
        password: formData.password
      };

      const response = await api.post("/auth/signup", payload);

      if (response.data.success) {
        setSuccess(true);
        toast(response.data.message || "Account created successfully!", "success");
      }
    } catch (error) {
      console.error("Signup error:", error);
      const message = error.response?.data?.message || "Failed to create account";
      setServerError(message);
      toast(message, "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      wide
      aside={<SignupAside />}
      subtitle={success ? "Your account is ready" : "Create your property management account"}
      footnote={success ? null : "After signing up, share payment evidence with Gamrent admin to activate your dashboard."}
    >
      {!success ? (
        <>
          <AuthTabs active="signup" onSignIn={() => navigate("/login")} onSignUp={() => {}} />

          <div className="hidden lg:block">
            <h2 className="text-xl font-bold text-slate-900">Create your account</h2>
            <p className="mt-1 text-sm text-slate-500">It only takes a minute. Fields marked * are required.</p>
          </div>

          <form onSubmit={handleSubmit} noValidate className="space-y-6">
            {serverError ? <AuthMessage>{serverError}</AuthMessage> : null}

            <fieldset className="space-y-4">
              <legend className="mb-3"><SectionLabel>Company</SectionLabel></legend>
              <div>
                <label htmlFor="signup-company" className={authLabelClass}>
                  Company Name <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <AuthInputIcon>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 21h18M5 21V7l7-4 7 4v14M9 21v-4h6v4M9 10h.01M15 10h.01M9 14h.01M15 14h.01" />
                  </AuthInputIcon>
                  <input
                    id="signup-company"
                    type="text"
                    name="companyName"
                    value={formData.companyName}
                    onChange={handleChange}
                    placeholder="e.g. Sunrise Properties"
                    autoComplete="organization"
                    className={fieldClass("companyName", "pl-11")}
                    {...fieldA11y("companyName")}
                  />
                </div>
                <AuthFieldError id="signup-companyName-error">{errors.companyName}</AuthFieldError>
              </div>
            </fieldset>

            <fieldset className="space-y-4">
              <legend className="mb-3"><SectionLabel>Account owner</SectionLabel></legend>
              <div className="grid grid-cols-1 min-[400px]:grid-cols-2 gap-4">
                <div className="min-w-0">
                  <label htmlFor="signup-first" className={authLabelClass}>
                    First Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="signup-first"
                    type="text"
                    name="adminFirstName"
                    value={formData.adminFirstName}
                    onChange={handleChange}
                    placeholder="First name"
                    autoComplete="given-name"
                    className={fieldClass("adminFirstName")}
                    {...fieldA11y("adminFirstName")}
                  />
                  <AuthFieldError id="signup-adminFirstName-error">{errors.adminFirstName}</AuthFieldError>
                </div>
                <div className="min-w-0">
                  <label htmlFor="signup-last" className={authLabelClass}>
                    Last Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="signup-last"
                    type="text"
                    name="adminLastName"
                    value={formData.adminLastName}
                    onChange={handleChange}
                    placeholder="Last name"
                    autoComplete="family-name"
                    className={fieldClass("adminLastName")}
                    {...fieldA11y("adminLastName")}
                  />
                  <AuthFieldError id="signup-adminLastName-error">{errors.adminLastName}</AuthFieldError>
                </div>
              </div>

              <div>
                <label htmlFor="signup-phone" className={authLabelClass}>
                  Phone <span className="font-normal text-slate-400">(optional)</span>
                </label>
                <div className="grid grid-cols-[minmax(0,10rem)_minmax(0,1fr)] gap-3">
                  <select
                    name="countryCode"
                    value={formData.countryCode}
                    onChange={handleChange}
                    aria-label="Country code"
                    autoComplete="tel-country-code"
                    className={`${authInputClass} px-3`}
                  >
                    {COUNTRY_CODE_OPTIONS.map((item) => (
                      <option key={item.code} value={item.code}>{item.label}</option>
                    ))}
                  </select>
                  <input
                    id="signup-phone"
                    type="tel"
                    inputMode="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="1234567"
                    autoComplete="tel-national"
                    className={fieldClass("phone")}
                    {...fieldA11y("phone", "signup-phone-hint")}
                  />
                </div>
                {errors.phone ? (
                  <AuthFieldError id="signup-phone-error">{errors.phone}</AuthFieldError>
                ) : (
                  <AuthFieldHint id="signup-phone-hint">Used for important account notices only.</AuthFieldHint>
                )}
              </div>
            </fieldset>

            <fieldset className="space-y-4">
              <legend className="mb-3"><SectionLabel>Sign-in details</SectionLabel></legend>
              <div>
                <label htmlFor="signup-email" className={authLabelClass}>
                  Email <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <AuthInputIcon>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </AuthInputIcon>
                  <input
                    id="signup-email"
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="you@company.com"
                    autoComplete="email"
                    inputMode="email"
                    className={fieldClass("email", "pl-11")}
                    {...fieldA11y("email", "signup-email-hint")}
                  />
                </div>
                {errors.email ? (
                  <AuthFieldError id="signup-email-error">{errors.email}</AuthFieldError>
                ) : (
                  <AuthFieldHint id="signup-email-hint">You'll use this email to sign in.</AuthFieldHint>
                )}
              </div>

              <div>
                <label htmlFor="signup-password" className={authLabelClass}>
                  Password <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <AuthInputIcon>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M7 11V7a5 5 0 0110 0v4M5 11h14a1 1 0 011 1v8a1 1 0 01-1 1H5a1 1 0 01-1-1v-8a1 1 0 011-1z" />
                  </AuthInputIcon>
                  <input
                    id="signup-password"
                    type={showPassword ? "text" : "password"}
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="Min. 8 characters"
                    autoComplete="new-password"
                    className={fieldClass("password", "pl-11 pr-12")}
                    {...fieldA11y("password")}
                  />
                  <PasswordVisibilityButton visible={showPassword} onToggle={() => setShowPassword(!showPassword)} />
                </div>
                <AuthFieldError id="signup-password-error">{errors.password}</AuthFieldError>
                <PasswordStrength value={formData.password} />
              </div>
            </fieldset>

            <button type="submit" disabled={loading} className={authPrimaryButtonClass}>
              {loading ? (
                <>
                  <Spinner />
                  Creating account...
                </>
              ) : (
                <>
                  Create Account
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 12h14M13 6l6 6-6 6" />
                  </svg>
                </>
              )}
            </button>

            <p className="text-center text-sm text-slate-500">
              Already have an account?{" "}
              <Link to="/login" className={authLinkClass}>Sign in</Link>
            </p>
          </form>
        </>
      ) : (
        <div className="text-center space-y-6">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-teal-50 ring-8 ring-teal-50/50">
            <CheckIcon className="w-8 h-8 text-teal-700" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-slate-900">Account created!</h2>
            <p className="mt-1 text-sm text-slate-500 break-words">
              <strong className="text-slate-800">{formData.companyName}</strong> is registered. Sign in with{" "}
              <strong className="text-slate-800">{formData.email}</strong>.
            </p>
          </div>

          <div className="text-left rounded-2xl border border-slate-200 bg-slate-50 p-5">
            <p className="text-sm font-semibold text-slate-900">What happens next</p>
            <ol className="mt-3 space-y-3">
              {NEXT_STEPS.map((step, index) => (
                <li key={step} className="flex items-start gap-3 text-sm text-slate-600">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-teal-700 text-xs font-bold text-white">
                    {index + 1}
                  </span>
                  <span className="pt-0.5">{step}</span>
                </li>
              ))}
            </ol>
          </div>

          <button
            onClick={() => navigate("/login", { state: { email: formData.email } })}
            className={authPrimaryButtonClass}
          >
            Go to Sign In
          </button>

          <button onClick={() => navigate("/")} className={authLinkClass}>
            ← Back to Website
          </button>
        </div>
      )}
    </AuthShell>
  );
};

export default Signup;
